import { useState, useMemo } from 'react'
import {
  Search, Download, LogIn, UserPlus, Trash2, Edit3,
  FileText, Bell, Crown, HelpCircle, Users, Shield,
  Settings, Calendar, Eye, Package, RefreshCw, Clock,
  Filter, ChevronDown, ChevronLeft, ChevronRight, X
} from 'lucide-react'

/* ─── Mock Data ─────────────────────────────────────────────── */
const ALL_LOGS = [
  { id: 1,  action: 'Admin Login',            desc: 'Successful login from 192.168.1.1',              user: 'admin@example.com',    module: 'System',              type: 'Login',  time: '2 min ago',   color: 'green',  icon: LogIn,      badge: 'success' },
  { id: 2,  action: 'User Created',           desc: 'New user "rahul@test.com" registered',            user: 'admin@example.com',    module: 'User Management',     type: 'Create', time: '15 min ago',  color: 'blue',   icon: UserPlus,   badge: 'info' },
  { id: 3,  action: 'Invoice Deleted',        desc: 'Invoice #INV-2041 permanently removed',           user: 'subadmin@example.com', module: 'Business Management', type: 'Delete', time: '32 min ago',  color: 'red',    icon: Trash2,     badge: 'danger' },
  { id: 4,  action: 'Role Updated',           desc: 'Role "Editor" permissions modified',              user: 'admin@example.com',    module: 'User Management',     type: 'Update', time: '1 hr ago',    color: 'amber',  icon: Edit3,      badge: 'warning' },
  { id: 5,  action: 'Report Exported',        desc: 'Monthly analytics report (PDF) exported',         user: 'analyst@example.com',  module: 'Analytics',           type: 'Export', time: '2 hr ago',    color: 'purple', icon: Download,   badge: 'purple' },
  { id: 6,  action: 'Notification Sent',      desc: 'Push notification sent to 1,240 users',           user: 'admin@example.com',    module: 'Communication',       type: 'Create', time: '3 hr ago',    color: 'blue',   icon: Bell,       badge: 'info' },
  { id: 7,  action: 'Subscription Cancelled', desc: 'User "priya@mail.com" cancelled Pro plan',        user: 'system',               module: 'Business Management', type: 'Update', time: '4 hr ago',    color: 'amber',  icon: Crown,      badge: 'warning' },
  { id: 8,  action: 'FAQ Added',              desc: 'New FAQ entry "How to reset password?" added',    user: 'subadmin@example.com', module: 'System',              type: 'Create', time: '5 hr ago',    color: 'green',  icon: HelpCircle, badge: 'success' },
  { id: 9,  action: 'Bulk Users Deleted',     desc: '12 inactive accounts permanently removed',         user: 'admin@example.com',    module: 'User Management',     type: 'Delete', time: '6 hr ago',    color: 'red',    icon: Users,      badge: 'danger' },
  { id: 10, action: 'Settings Updated',       desc: 'Email SMTP configuration changed',                user: 'admin@example.com',    module: 'System',              type: 'Update', time: '8 hr ago',    color: 'amber',  icon: Settings,   badge: 'warning' },
  { id: 11, action: 'Event Created',          desc: 'Team meeting scheduled for Jul 20',               user: 'subadmin@example.com', module: 'Planning',            type: 'Create', time: '10 hr ago',   color: 'blue',   icon: Calendar,   badge: 'info' },
  { id: 12, action: 'Feedback Viewed',        desc: 'Support ticket #4892 reviewed',                   user: 'admin@example.com',    module: 'Communication',       type: 'View',   time: 'Yesterday',   color: 'gray',   icon: Eye,        badge: 'neutral' },
  { id: 13, action: 'Item Added',             desc: 'Product "Premium Bundle" added to catalog',        user: 'admin@example.com',    module: 'Business Management', type: 'Create', time: 'Yesterday',   color: 'green',  icon: Package,    badge: 'success' },
  { id: 14, action: 'Sub Admin Created',      desc: 'New sub-admin "ops@company.com" added',           user: 'admin@example.com',    module: 'User Management',     type: 'Create', time: '2 days ago',  color: 'blue',   icon: Shield,     badge: 'info' },
  { id: 15, action: 'Data Export',            desc: 'User list CSV exported (2,430 records)',           user: 'analyst@example.com',  module: 'Analytics',           type: 'Export', time: '2 days ago',  color: 'purple', icon: Download,   badge: 'purple' },
  { id: 16, action: 'Password Reset',         desc: 'Password reset link sent to user@mail.com',       user: 'system',               module: 'System',              type: 'Update', time: '3 days ago',  color: 'amber',  icon: RefreshCw,  badge: 'warning' },
  { id: 17, action: 'File Uploaded',          desc: 'Profile photo updated by john@example.com',       user: 'john@example.com',     module: 'User Management',     type: 'Update', time: '3 days ago',  color: 'blue',   icon: FileText,   badge: 'info' },
  { id: 18, action: 'Admin Logout',           desc: 'Session ended — admin@example.com',               user: 'admin@example.com',    module: 'System',              type: 'Login',  time: '4 days ago',  color: 'gray',   icon: LogIn,      badge: 'neutral' },
]

const TABS      = ['All', 'Login', 'Create', 'Update', 'Delete', 'Export', 'View']
const MODULES   = ['All Modules', 'User Management', 'Business Management', 'Analytics', 'Communication', 'System', 'Planning']
const DATE_RANGES = ['All Time', 'Today', 'Last 7 Days', 'Last 30 Days']

/* ─── Color / Badge Maps ─────────────────────────────────────── */
const iconBg = {
  green:  { bg: 'var(--color-green-glow)',  color: 'var(--color-green)' },
  blue:   { bg: 'rgba(59,130,246,.12)',     color: 'var(--blue)' },
  red:    { bg: 'var(--color-red-glow)',    color: 'var(--color-red)' },
  amber:  { bg: 'var(--color-amber-glow)', color: 'var(--color-amber)' },
  purple: { bg: 'var(--color-purple-glow)',color: 'var(--color-purple)' },
  gray:   { bg: 'var(--border-subtle)',    color: 'var(--text3)' },
}

const badgeClass = {
  success: 'badge-active',
  info:    'badge-info',
  danger:  'badge-danger',
  warning: 'badge-pending',
  purple:  'badge-vip',
  neutral: 'badge-gold',
}

/* ─── Reusable Pagination ────────────────────────────────────── */
function Pagination({ page, totalPages, total, perPage, onPageChange, onPerPageChange }) {
  const from = total === 0 ? 0 : (page - 1) * perPage + 1
  const to   = Math.min(page * perPage, total)

  const pages = []
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i)
  } else {
    pages.push(1)
    if (page > 3) pages.push('...')
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i)
    if (page < totalPages - 2) pages.push('...')
    pages.push(totalPages)
  }

  const btnBase = {
    minWidth: 32, height: 32, padding: '0 6px',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    background: 'transparent', cursor: 'pointer', fontSize: 'var(--fs-xs)',
    fontFamily: 'var(--font-body)', color: 'var(--text2)',
    transition: 'all 150ms ease',
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '12px var(--space-lg)', borderTop: '1px solid var(--border)',
      flexWrap: 'wrap', gap: 8,
    }}>
      {/* Left: info + per-page */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>
        <span>Showing <b style={{ color: 'var(--text2)' }}>{from}–{to}</b> of <b style={{ color: 'var(--text2)' }}>{total}</b></span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>Rows:</span>
          <select
            value={perPage}
            onChange={e => onPerPageChange(Number(e.target.value))}
            style={{
              background: 'var(--bg-raised)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', color: 'var(--text2)',
              fontSize: 'var(--fs-xs)', fontFamily: 'var(--font-body)',
              padding: '2px 20px 2px 6px', cursor: 'pointer', outline: 'none',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat', backgroundPosition: 'right 5px center',
            }}
          >
            {[5, 10, 15, 20].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {/* Right: page buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button
          style={{ ...btnBase, opacity: page === 1 ? 0.4 : 1, cursor: page === 1 ? 'not-allowed' : 'pointer' }}
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
        >
          <ChevronLeft size={14} />
        </button>

        {pages.map((p, i) =>
          p === '...'
            ? <span key={`dots-${i}`} style={{ padding: '0 4px', color: 'var(--text3)', fontSize: 'var(--fs-xs)' }}>…</span>
            : <button
                key={p}
                onClick={() => onPageChange(p)}
                style={{
                  ...btnBase,
                  background: page === p ? 'var(--accent)' : 'transparent',
                  borderColor: page === p ? 'var(--accent)' : 'var(--border)',
                  color: page === p ? '#fff' : 'var(--text2)',
                  fontWeight: page === p ? 600 : 400,
                }}
              >{p}</button>
        )}

        <button
          style={{ ...btnBase, opacity: page === totalPages ? 0.4 : 1, cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  )
}

/* ─── Log Table Row ──────────────────────────────────────────── */
function LogRow({ log }) {
  const Icon = log.icon
  const clr  = iconBg[log.color] || iconBg.gray
  return (
    <tr>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            width: 34, height: 34, borderRadius: '50%',
            background: clr.bg, color: clr.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Icon size={15} strokeWidth={2} />
          </span>
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text1)', fontSize: 'var(--fs-base)', whiteSpace: 'nowrap' }}>{log.action}</div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)', marginTop: 1 }}>{log.desc}</div>
          </div>
        </div>
      </td>
      <td><span className={`badge ${badgeClass[log.badge]}`}>{log.type}</span></td>
      <td><span className="badge badge-gold">{log.module}</span></td>
      <td style={{ fontSize: 'var(--fs-sm)', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{log.user}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>
          <Clock size={12} />{log.time}
        </span>
      </td>
    </tr>
  )
}

/* ─── Select Dropdown ────────────────────────────────────────── */
function SelectInput({ value, onChange, options, icon: Icon }) {
  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      {Icon && (
        <Icon size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', pointerEvents: 'none' }} />
      )}
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          paddingLeft: Icon ? 28 : 10, paddingRight: 28, height: 34,
          background: 'var(--bg-raised)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', color: 'var(--text2)',
          fontSize: 'var(--fs-sm)', fontFamily: 'var(--font-body)',
          cursor: 'pointer', appearance: 'none', outline: 'none', whiteSpace: 'nowrap',
        }}
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      <ChevronDown size={12} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', pointerEvents: 'none' }} />
    </div>
  )
}

/* ─── Main Page ──────────────────────────────────────────────── */
export default function ActivityLogs() {
  const [activeTab,  setActiveTab]  = useState('All')
  const [search,     setSearch]     = useState('')
  const [module,     setModule]     = useState('All Modules')
  const [dateRange,  setDateRange]  = useState('All Time')
  const [page,       setPage]       = useState(1)
  const [perPage,    setPerPage]    = useState(10)

  const filtered = useMemo(() => {
    return ALL_LOGS.filter(l => {
      if (activeTab !== 'All' && l.type !== activeTab) return false
      if (module !== 'All Modules' && l.module !== module) return false
      const q = search.toLowerCase()
      if (q && !l.action.toLowerCase().includes(q) &&
               !l.desc.toLowerCase().includes(q) &&
               !l.user.toLowerCase().includes(q)) return false
      return true
    })
  }, [activeTab, search, module, dateRange])

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage))
  const safePage   = Math.min(page, totalPages)
  const paginated  = filtered.slice((safePage - 1) * perPage, safePage * perPage)

  const hasFilters = search || module !== 'All Modules' || dateRange !== 'All Time' || activeTab !== 'All'

  function resetPage() { setPage(1) }

  function clearFilters() {
    setSearch(''); setModule('All Modules')
    setDateRange('All Time'); setActiveTab('All'); setPage(1)
  }

  function exportCSV() {
    const header = 'Action,Description,User,Module,Type,Time'
    const rows = filtered.map(l => [l.action, `"${l.desc}"`, l.user, l.module, l.type, l.time].join(','))
    const csv  = [header, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url; a.download = 'activity-logs.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="page-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h1 className="page-title">Activity Logs</h1>
        <button className="btn btn-primary btn-sm" onClick={exportCSV} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Download size={13} /> Export CSV
        </button>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 0 }}>
          <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Search actions, users, descriptions…"
            value={search}
            onChange={e => { setSearch(e.target.value); resetPage() }}
            style={{ paddingLeft: 32, height: 34, fontSize: 'var(--fs-sm)' }}
          />
          {search && (
            <button onClick={() => { setSearch(''); resetPage() }} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', display: 'flex', padding: 0 }}>
              <X size={12} />
            </button>
          )}
        </div>
        <SelectInput value={module}    onChange={v => { setModule(v);    resetPage() }} options={MODULES}     icon={Filter} />
        <SelectInput value={dateRange} onChange={v => { setDateRange(v); resetPage() }} options={DATE_RANGES} icon={Clock}  />
        {hasFilters && (
          <button className="btn btn-outline btn-sm" onClick={clearFilters} style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
            <X size={12} /> Clear
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div className="view-tabs">
          {TABS.map(tab => (
            <button key={tab} className={`vtab${activeTab === tab ? ' active' : ''}`} onClick={() => { setActiveTab(tab); resetPage() }}>
              {tab}
            </button>
          ))}
        </div>
        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>
          {filtered.length} log{filtered.length !== 1 ? 's' : ''} found
        </span>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ minWidth: 260 }}>Action</th>
                <th style={{ width: 100 }}>Type</th>
                <th style={{ width: 160 }}>Module</th>
                <th style={{ width: 200 }}>User</th>
                <th style={{ width: 120 }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text3)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                      <Filter size={28} style={{ opacity: 0.3 }} />
                      <span style={{ fontSize: 'var(--fs-sm)' }}>No logs match your current filters</span>
                      <button className="btn btn-ghost btn-sm" onClick={clearFilters}>Clear filters</button>
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map(log => <LogRow key={log.id} log={log} />)
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          page={safePage}
          totalPages={totalPages}
          total={filtered.length}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={n => { setPerPage(n); setPage(1) }}
        />
      </div>

    </div>
  )
}